/**
 * IA Data Mapping Utilities
 * Maps CMS validation criteria to our internal format
 */

// ============================================================================
// Types for CMS Data Format
// ============================================================================

export interface CMSValidationCriteria {
  ID: string;
  'Subcategory Name': string | null;
  'Activity Name': string;
  'Activity Description': string;
  'Objective & Validation Documentation': string;
  'First PY': number;
}

export interface MappedValidationCriteria {
  measureId: string;
  subcategoryName: string | null;
  subcategoryId: string | null;
  activityName: string;
  activityDescription: string;
  validationDocumentation: string;
  firstPerformanceYear: number;
}

// ============================================================================
// Subcategory Mapping
// ============================================================================

export const IA_SUBCATEGORY_MAP: Record<string, { id: string; prefix: string }> = {
  'Achieving Health Equity': { id: 'AHE', prefix: 'IA_AHE' },
  'Beneficiary Engagement': { id: 'BE', prefix: 'IA_BE' },
  'Behavioral and Mental Health': { id: 'BMH', prefix: 'IA_BMH' },
  'Care Coordination': { id: 'CC', prefix: 'IA_CC' },
  'Expanded Practice Access': { id: 'EPA', prefix: 'IA_EPA' },
  'Emergency Response and Preparedness': { id: 'ERP', prefix: 'IA_ERP' },
  'Population Management': { id: 'PM', prefix: 'IA_PM' },
  'Patient Safety and Practice Assessment': { id: 'PSPA', prefix: 'IA_PSPA' },
};

export const IA_SUBCATEGORIES = [
  { id: 'all', name: 'All Categories', prefix: '' },
  { id: 'AHE', name: 'Achieving Health Equity', prefix: 'IA_AHE' },
  { id: 'BE', name: 'Beneficiary Engagement', prefix: 'IA_BE' },
  { id: 'BMH', name: 'Behavioral & Mental Health', prefix: 'IA_BMH' },
  { id: 'CC', name: 'Care Coordination', prefix: 'IA_CC' },
  { id: 'EPA', name: 'Expanded Practice Access', prefix: 'IA_EPA' },
  { id: 'ERP', name: 'Emergency Response', prefix: 'IA_ERP' },
  { id: 'PM', name: 'Population Management', prefix: 'IA_PM' },
  { id: 'PSPA', name: 'Patient Safety', prefix: 'IA_PSPA' },
];

// ============================================================================
// Mapping Functions
// ============================================================================

/**
 * Map CMS validation criteria format to our internal format
 */
export function mapValidationCriteria(criteria: CMSValidationCriteria): MappedValidationCriteria {
  const subcategoryName = criteria['Subcategory Name'];
  const subcategoryInfo = subcategoryName ? IA_SUBCATEGORY_MAP[subcategoryName] : null;
  
  return {
    measureId: criteria.ID,
    subcategoryName: subcategoryName,
    subcategoryId: subcategoryInfo?.id || null,
    activityName: criteria['Activity Name'],
    activityDescription: criteria['Activity Description'],
    validationDocumentation: criteria['Objective & Validation Documentation'],
    firstPerformanceYear: criteria['First PY'],
  };
}

/**
 * Map array of CMS validation criteria
 */
export function mapAllValidationCriteria(criteria: CMSValidationCriteria[]): MappedValidationCriteria[] {
  return criteria.map(mapValidationCriteria);
}

/**
 * Get subcategory ID from activity ID (e.g., 'IA_EPA_2' -> 'EPA')
 */
export function getSubcategoryIdFromActivityId(activityId: string): string | null {
  // Extract the subcategory prefix (e.g., 'EPA' from 'IA_EPA_2')
  const match = activityId.match(/^IA_([A-Z]+)_/);
  if (match) {
    return match[1];
  }
  
  // Handle special cases like IA_PCMH, IA_MVP
  if (activityId === 'IA_PCMH' || activityId === 'IA_MVP') {
    return null; // No subcategory
  }
  
  return null;
}

/**
 * Get subcategory name from ID
 */
export function getSubcategoryName(subcategoryId: string | null): string {
  if (!subcategoryId) return 'Other';
  const subcat = IA_SUBCATEGORIES.find(s => s.id === subcategoryId);
  return subcat?.name || 'Other';
}

/**
 * Get full subcategory info from ID
 */
export function getSubcategoryInfo(subcategoryId: string): typeof IA_SUBCATEGORIES[0] | undefined {
  return IA_SUBCATEGORIES.find(s => s.id === subcategoryId);
}

// ============================================================================
// Practice Status Helpers
// ============================================================================

export interface PracticeStatus {
  isSmallPractice: boolean;
  isRural: boolean;
  isHPSA: boolean;
  isNonPatientFacing: boolean;
}

/**
 * Get IA requirements based on practice status
 */
export function getIARequirementsForPractice(status: PracticeStatus): {
  activitiesRequired: number;
  pointsPerActivity: number;
  maxPoints: number;
  isSpecialStatus: boolean;
  statusLabel: string | null;
} {
  const isSpecialStatus = status.isSmallPractice || status.isRural || status.isHPSA || status.isNonPatientFacing;
  
  let statusLabel: string | null = null;
  if (status.isSmallPractice) statusLabel = 'Small Practice (≤15 clinicians)';
  else if (status.isRural) statusLabel = 'Rural';
  else if (status.isHPSA) statusLabel = 'HPSA';
  else if (status.isNonPatientFacing) statusLabel = 'Non-Patient Facing';
  
  return {
    activitiesRequired: isSpecialStatus ? 1 : 2,
    pointsPerActivity: isSpecialStatus ? 40 : 20,
    maxPoints: 40,
    isSpecialStatus,
    statusLabel,
  };
}

// ============================================================================
// Validation Criteria Parsing
// ============================================================================

/**
 * Parse validation documentation into sections
 * The CMS validation documentation typically has:
 * - Objective: ...
 * - Validation Documentation: ...
 * - Information: ... (optional)
 * - Example(s): ... (optional)
 */
export function parseValidationDocumentation(documentation: string): {
  objective: string;
  validationRequirements: string[];
  information: string | null;
  examples: string | null;
} {
  const sections = {
    objective: '',
    validationRequirements: [] as string[],
    information: null as string | null,
    examples: null as string | null,
  };
  
  if (!documentation) return sections;
  
  // Extract Objective
  const objectiveMatch = documentation.match(/Objective:\s*([\s\S]*?)(?=\n\nValidation|$)/i);
  if (objectiveMatch) {
    sections.objective = objectiveMatch[1].trim();
  }
  
  // Extract Validation Documentation requirements
  const validationMatch = documentation.match(/Validation Documentation:\s*([\s\S]*?)(?=\n\nInformation:|Example\(s\):|$)/i);
  if (validationMatch) {
    const validationText = validationMatch[1].trim();
    // Split by numbered items (1), 2), etc.) or bullet points
    const requirements = validationText.split(/(?:\d+\)|•|\n-)\s*/);
    sections.validationRequirements = requirements
      .filter(r => r.trim().length > 0)
      .map(r => r.trim().replace(/;\s*(?:AND|OR)\s*$/i, '').trim());
  }
  
  // Extract Information (optional)
  const infoMatch = documentation.match(/Information:\s*([\s\S]*?)(?=Example\(s\):|$)/i);
  if (infoMatch) {
    sections.information = infoMatch[1].trim();
  }
  
  // Extract Examples (optional)
  const examplesMatch = documentation.match(/Example\(s\):\s*([\s\S]*?)(?=Information:|$)/i);
  if (examplesMatch) {
    sections.examples = examplesMatch[1].trim();
  }
  
  return sections;
}

// ============================================================================
// Activity Display Helpers
// ============================================================================

/**
 * Get badge color class for subcategory
 */
export function getSubcategoryBadgeColor(subcategoryId: string | null): string {
  const colors: Record<string, string> = {
    'AHE': 'bg-purple-100 text-purple-800 border-purple-200',
    'BE': 'bg-blue-100 text-blue-800 border-blue-200',
    'BMH': 'bg-pink-100 text-pink-800 border-pink-200',
    'CC': 'bg-green-100 text-green-800 border-green-200',
    'EPA': 'bg-orange-100 text-orange-800 border-orange-200',
    'ERP': 'bg-red-100 text-red-800 border-red-200',
    'PM': 'bg-cyan-100 text-cyan-800 border-cyan-200',
    'PSPA': 'bg-amber-100 text-amber-800 border-amber-200',
  };
  
  if (!subcategoryId) return 'bg-gray-100 text-gray-800 border-gray-200';
  return colors[subcategoryId] || 'bg-gray-100 text-gray-800 border-gray-200';
}

/**
 * Get short activity name for display
 */
export function getShortActivityName(fullName: string, maxLength: number = 50): string {
  if (fullName.length <= maxLength) return fullName;
  return fullName.substring(0, maxLength - 3) + '...';
}

/**
 * Format activity ID for display (e.g., 'IA_EPA_2' -> 'EPA-2')
 */
export function formatActivityId(activityId: string): string {
  const match = activityId.match(/^IA_([A-Z]+)_(\d+)$/);
  if (match) {
    return `${match[1]}-${match[2]}`;
  }
  // Handle special cases
  if (activityId === 'IA_PCMH') return 'PCMH';
  if (activityId === 'IA_MVP') return 'MVP';
  return activityId.replace('IA_', '');
}

